/**
 * Avatar component - Circular avatar with initial letter
 * Factory function following the app's architectural patterns
 */
import { gravatarUrl } from "@utils/hash.js";

export interface AvatarConfig {
  readonly initial?: string;
  readonly size?: number;
  readonly backgroundColor?: string;
  readonly textColor?: string;
  readonly fontSize?: number;
  readonly className?: string;
  readonly onClick?: () => void;
  readonly name?: string; // Used to derive initial if provided
  readonly email?: string; // Used to fetch gravatar when no avatarUrl
  readonly avatarUrl?: string; // Direct URL if available
}

export interface AvatarAPI {
  readonly getElement: () => HTMLElement;
  readonly setInitial: (initial: string) => void;
  readonly setBackgroundColor: (color: string) => void;
  readonly setImageUrl: (url: string | null) => void;
  readonly setEmail: (email: string | null) => void;
  readonly setName: (name: string | null) => void;
  readonly destroy: () => void;
}

/**
 * Creates Avatar component with circular design and initial letter
 * @param config - Configuration for the avatar
 * @returns Avatar API
 */
export const createAvatar = (config: AvatarConfig = {}): AvatarAPI => {
  const {
    initial = "U",
    size = 44,
    backgroundColor = "var(--color-dark)",
    textColor = "var(--color-text--inverse)",
    fontSize = 14,
    className = "",
    onClick,
    name,
    email,
    avatarUrl,
  } = config;

  let isDestroyed = false;
  let currentInitial = (name?.trim()?.charAt(0) || initial)
    .toUpperCase()
    .charAt(0);
  let currentEmail: string | null = email ? String(email) : null;
  let currentImageUrl: string | null = avatarUrl ? String(avatarUrl) : null;

  // Create avatar element
  const avatar = document.createElement("div");
  avatar.className = `avatar ${className}`.trim();

  // Set initial styles
  avatar.style.width = `${size}px`;
  avatar.style.height = `${size}px`;
  avatar.style.backgroundColor = backgroundColor;
  avatar.style.color = textColor;
  avatar.style.fontSize = `${fontSize}px`;
  // Text node for initial fallback
  const initialSpan = document.createElement("span");
  initialSpan.textContent = currentInitial;
  avatar.appendChild(initialSpan);

  // Image element for avatar/gravatar
  const img = document.createElement("img");
  img.alt = "avatar";
  img.style.width = "100%";
  img.style.height = "100%";
  img.style.objectFit = "cover";
  img.style.borderRadius = "50%";
  img.style.display = "none"; // hidden by default until loaded
  avatar.appendChild(img);

  // Add click handler if provided
  if (onClick) {
    avatar.style.cursor = "pointer";
    avatar.addEventListener("click", onClick);
  }

  const showInitial = (): void => {
    img.style.display = "none";
    initialSpan.style.display = "inline";
    initialSpan.textContent = currentInitial;
  };

  const showImage = (url: string): void => {
    // Attach temporary listeners for load/error to control visibility
    const handleLoad = () => {
      img.style.display = "block";
      initialSpan.style.display = "none";
      cleanupListeners();
    };
    const handleError = () => {
      // If primary image fails and we have an email, try gravatar once.
      // Note: gravatarUrl uses d=404 by default so we'll fall back to initials if missing.
      const isGravatar = url.includes("gravatar.com/avatar/");
      if (!isGravatar && currentEmail && currentEmail.trim() !== "") {
        const sizePx = Math.max(32, size * 2);
        const gUrl = gravatarUrl(currentEmail.trim().toLowerCase(), sizePx);
        cleanupListeners();
        // Attempt gravatar; if it fails too, its own error handler will fallback to initial
        showImage(gUrl);
        return;
      }
      showInitial();
      cleanupListeners();
    };
    const cleanupListeners = () => {
      img.removeEventListener("load", handleLoad);
      img.removeEventListener("error", handleError);
    };

    img.addEventListener("load", handleLoad);
    img.addEventListener("error", handleError);
    img.src = url;
  };

  // (removed inline md5)

  const applySource = (): void => {
    if (isDestroyed) return;
    if (currentImageUrl && currentImageUrl.trim() !== "") {
      showImage(currentImageUrl);
      return;
    }
    if (currentEmail && currentEmail.trim() !== "") {
      const sizePx = Math.max(32, size * 2);
      // Use "mp" default to avoid 404s; gravatar will provide a placeholder
      const url = gravatarUrl(currentEmail.trim().toLowerCase(), sizePx, "mp");
      showImage(url);
      return;
    }
    showInitial();
  };

  // Initial render selection
  applySource();

  /**
   * Update the initial letter
   */
  const setInitial = (newInitial: string): void => {
    if (isDestroyed) return;

    currentInitial = newInitial.toUpperCase().charAt(0);
    initialSpan.textContent = currentInitial;
    if (!currentImageUrl && !currentEmail) showInitial();
  };

  /**
   * Update background color
   */
  const setBackgroundColor = (color: string): void => {
    if (isDestroyed) return;

    avatar.style.backgroundColor = color;
  };

  const setImageUrl = (url: string | null): void => {
    if (isDestroyed) return;
    currentImageUrl = url && url.trim() !== "" ? url : null;
    applySource();
  };

  const setEmail = (nextEmail: string | null): void => {
    if (isDestroyed) return;
    currentEmail = nextEmail && nextEmail.trim() !== "" ? nextEmail : null;
    applySource();
  };

  const setName = (nextName: string | null): void => {
    if (isDestroyed) return;
    if (nextName && nextName.trim()) {
      currentInitial = nextName.trim().charAt(0).toUpperCase();
      initialSpan.textContent = currentInitial;
      if (!currentImageUrl) showInitial();
    }
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Remove event listeners
    if (onClick) {
      avatar.removeEventListener("click", onClick);
    }

    // Remove from DOM if still connected
    if (avatar.parentNode) {
      avatar.parentNode.removeChild(avatar);
    }
  };

  return {
    getElement: () => avatar,
    setInitial,
    setBackgroundColor,
    setImageUrl,
    setEmail,
    setName,
    destroy,
  };
};

/**
 * Creates Avatar component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the avatar
 * @returns Avatar API
 */
export const createAvatarInContainer = (
  container: HTMLElement,
  config: AvatarConfig = {}
): AvatarAPI => {
  const avatar = createAvatar(config);
  container.appendChild(avatar.getElement());
  return avatar;
};
